"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAutoTransformSchema = exports.createAutoTransformForm = void 0;
exports.isAutoTransformParams = isAutoTransformParams;
const parameter_specs_1 = require("@karya/parameter-specs");
exports.createAutoTransformForm = [
    {
        label: 'Basic Transform Information',
        description: 'Provide some basic information about the task you are about to create',
        required: true,
        parameters: [
            {
                id: 'name',
                label: 'Task Name',
                description: 'Name of the task in English. This will be displayed only in the task dashboard',
                type: 'string',
                required: true,
            },
            {
                id: 'description',
                label: 'Task Description',
                description: 'Detailed description of the task.',
                type: 'string',
                long: true,
                required: true,
            },
            {
                id: 'tags',
                label: 'Task Tags',
                description: 'A set of string tokens to associate with this task. These tokens must uniquely identify this task or a set of related tasks',
                type: 'list',
                required: true,
            },
        ],
    },
    {
        label: 'Batching Parameters',
        description: 'These parameters determine how the Autotransform is batched',
        required: true,
        parameters: [
            {
                id: 'batchAutoTransform',
                label: 'Batch Auto Transform',
                description: 'Indicates that this task is a batch auto transform task, that can operate on multiple inputs at once',
                type: 'boolean',
                required: false,
            },
            {
                id: 'autotransformBatchSize',
                label: 'Batch Size',
                description: 'The number of microtasks to be batched together for processing',
                type: 'int',
                required: false,
                requires: [['batchAutoTransform', true]],
                requiredIfSatisfied: true,
            },
        ],
    },
    {
        label: 'Validation Parameters',
        description: 'These parameters determine how assignments are validated and what happens to rejected assignments',
        required: true,
        parameters: [
            {
                id: 'isValidationTask',
                label: 'Validation Task',
                description: 'Indicates that this task is a validation task for assignments of another task. In this case, the output of the task is required to have two parameters: an accepted flag that indicates if the source assignment response is accepted, and a fraction score indicating what fraction of the bonus credits should be paid out to the user',
                type: 'boolean',
                required: false,
            },
        ],
    },
    {
        label: 'Output Storage',
        description: 'These parameters define at what granularity and where the output are generated',
        required: true,
        parameters: [
            {
                id: 'outputGenGranularity',
                label: 'Output Generation Granularity',
                description: 'This parameter determines if the output should be generated for each microtask or for a response of each microtask. For example, if you are generating multiple audio recordings for a given prompt, then you must set this to each response. On the other hand, it you are transcribing an audio file, then there is only one output, and you must set the value to microtask.',
                type: 'fixed',
                value: 'MICROTASK',
                required: false,
            },
            {
                id: 'outputDatasetName',
                label: 'Output Dataset Name',
                description: 'If you want to create a new dataset, enter the name for the newly created output dataset.',
                type: 'string',
                required: true,
            },
            {
                id: 'enableOutputDatapointInputKeysFilter',
                label: 'Filter Input Keys in Output Datapoints',
                description: 'Enable this to selectively choose which input keys to include in output datapoints. This helps reduce output datapoint size by excluding unnecessary input keys. When disabled, all input keys are included.',
                type: 'boolean',
                required: false,
            },
            {
                id: 'outputDatapointInputKeys',
                label: 'Select Input Keys to Include',
                description: 'Specify which input keys to include in output datapoints. Example: {"data": ["sentence_id", "text"], "files": ["audio"]}. Leave data or files empty to exclude all keys from that section.',
                type: 'json',
                required: false,
                requires: [['enableOutputDatapointInputKeysFilter', true]],
                requiredIfSatisfied: true,
            },
            {
                id: 'shouldCreateMultipleDatapoints',
                label: 'Create Multiple Datapoints',
                description: 'When enabled, the output of the task will be used to create multiple datapoints.',
                type: 'boolean',
                required: false,
            },
            {
                id: 'splitField',
                label: 'Split Field',
                description: 'The field in the output data that contains the split data. This field must be an array.',
                type: 'string',
                required: false,
                requires: [['shouldCreateMultipleDatapoints', true]],
                requiredIfSatisfied: true,
            },
        ],
    },
    {
        label: 'Task Specification',
        description: 'The task specification is a description of the data flow and is used to render the chat based interaction between the user and the karya app',
        required: false,
        parameters: [
            {
                id: 'spec',
                label: 'Task Specification',
                description: 'Import task specification from a file',
                type: 'json_object',
                renderer: 'task-spec-builder',
                required: true,
            },
        ],
    },
];
function isAutoTransformParams(params) {
    return params.taskType == 'AUTO';
}
// Export the joi schema
const autoTransformParameterArray = (0, parameter_specs_1.parameterArrayFromGroups)(exports.createAutoTransformForm);
exports.createAutoTransformSchema = (0, parameter_specs_1.joiSchema)(autoTransformParameterArray, {
    isKeyPath: true,
});
//# sourceMappingURL=Create.js.map